// DashboardMissionControl.js - Dashboard Variation A: "Mission Control"
// NASA/SOC inspired with central gauge and satellite metrics

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    ResponsiveContainer,
    AreaChart,
    Area
} from 'recharts';
import {
    getCommandCenterSummary,
    getRiskLevelColor,
    getRiskLevelLabel,
    formatRelativeTime,
    getSeverityColor
} from '../../services/commandCenterService';
import { useThemeColors } from '../../hooks/useThemeColors';
import { SkeletonDashboard } from '../../components/common';
import './DashboardMissionControl.css';

// Gauge component for central risk score
function RiskGauge({ score, riskLevel }) {
    const circumference = 2 * Math.PI * 85;
    const progress = (score / 100) * circumference;

    return (
        <div className="mc-gauge">
            <svg viewBox="0 0 200 200" className="mc-gauge-svg">
                {/* Background circle */}
                <circle
                    cx="100"
                    cy="100"
                    r="85"
                    fill="none"
                    stroke="var(--border-dark)"
                    strokeWidth="12"
                />
                {/* Progress arc */}
                <circle
                    cx="100"
                    cy="100"
                    r="85"
                    fill="none"
                    stroke={getRiskLevelColor(riskLevel)}
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${progress} ${circumference}`}
                    strokeDashoffset={circumference / 4}
                    transform="rotate(-90 100 100)"
                    className="mc-gauge-progress"
                />
                {/* Center content */}
                <text
                    x="100"
                    y="90"
                    textAnchor="middle"
                    className="mc-gauge-score"
                    fill={getRiskLevelColor(riskLevel)}
                >
                    {score}
                </text>
                <text
                    x="100"
                    y="115"
                    textAnchor="middle"
                    className="mc-gauge-label"
                    fill="var(--text-secondary)"
                >
                    {getRiskLevelLabel(riskLevel)}
                </text>
            </svg>
            <div className="mc-gauge-title">SECURITY POSTURE</div>
        </div>
    );
}

// Alert ticker component
function AlertTicker({ alerts }) {
    if (!alerts || alerts.length === 0) return null;

    return (
        <div className="mc-ticker">
            <div className="mc-ticker-label">
                <span className="ticker-icon">🚨</span>
                ALERTS
            </div>
            <div className="mc-ticker-content">
                <div className="mc-ticker-scroll">
                    {alerts.map((alert, i) => (
                        <span key={i} className="ticker-item">
                            <span className={`ticker-severity severity-${alert.severity?.toLowerCase()}`}>
                                {alert.severity}
                            </span>
                            {alert.title} on {alert.asset}
                            <span className="ticker-separator">•</span>
                        </span>
                    ))}
                    {/* Duplicate for seamless scroll */}
                    {alerts.map((alert, i) => (
                        <span key={`dup-${i}`} className="ticker-item">
                            <span className={`ticker-severity severity-${alert.severity?.toLowerCase()}`}>
                                {alert.severity}
                            </span>
                            {alert.title} on {alert.asset}
                            <span className="ticker-separator">•</span>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Satellite metric card
function MetricCard({ icon, value, label, color, onClick, pulse }) {
    return (
        <div
            className={`mc-metric-card ${pulse ? 'pulse' : ''}`}
            onClick={onClick}
            style={{ '--card-accent': color }}
        >
            <div className="mc-metric-icon">{icon}</div>
            <div className="mc-metric-value" style={{ color }}>{value}</div>
            <div className="mc-metric-label">{label}</div>
        </div>
    );
}

// Recommended action item
function ActionItem({ action, index }) {
    const getPriorityColor = (priority) => {
        if (priority === 'critical' || priority === 1) return 'var(--severity-critical)';
        if (priority === 'high' || priority === 2) return 'var(--severity-high)';
        if (priority === 'medium' || priority === 3) return 'var(--severity-medium)';
        return 'var(--text-secondary)';
    };

    return (
        <div className="mc-action-item">
            <div className="action-rank" style={{ color: getPriorityColor(index + 1) }}>
                {index + 1}
            </div>
            <div className="action-content">
                <div className="action-title">{action.title}</div>
                <div className="action-meta">
                    <span className="action-asset">{action.asset}</span>
                    <span className="action-severity" style={{ color: getSeverityColor(action.severity) }}>
                        {action.severity?.toUpperCase()}
                    </span>
                </div>
            </div>
            <button className="action-btn" onClick={() => action.onClick?.()}>
                View →
            </button>
        </div>
    );
}

// Asset row for the asset scroll
function AssetRow({ asset, onClick }) {
    const getRiskDotClass = (level) => {
        if (level === 'critical') return 'dot-critical';
        if (level === 'atRisk') return 'dot-atrisk';
        if (level === 'caution') return 'dot-caution';
        return 'dot-healthy';
    };

    return (
        <div className="mc-asset-row" onClick={onClick}>
            <div className={`asset-dot ${getRiskDotClass(asset.riskLevel)}`} />
            <div className="asset-name">{asset.value}</div>
            <div className="asset-score">{asset.riskScore}</div>
            <div className="asset-vulns">
                {asset.vulnCounts?.critical > 0 && (
                    <span className="vuln-badge critical">{asset.vulnCounts.critical}C</span>
                )}
                {asset.vulnCounts?.high > 0 && (
                    <span className="vuln-badge high">{asset.vulnCounts.high}H</span>
                )}
                {asset.vulnCounts?.medium > 0 && (
                    <span className="vuln-badge medium">{asset.vulnCounts.medium}M</span>
                )}
            </div>
            <div className="asset-time">{formatRelativeTime(asset.lastScanAt)}</div>
            <div className="asset-actions">
                <button className="asset-action-btn">View</button>
                <button className="asset-action-btn secondary">Scan</button>
            </div>
        </div>
    );
}

function DashboardMissionControl() {
    const navigate = useNavigate();
    const themeColors = useThemeColors();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        summary: {},
        assets: [],
        topRisks: [],
        trends: {}
    });

    // Fetch command center data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const result = await getCommandCenterSummary();
                if (result.success) {
                    setData({
                        summary: result.summary || {},
                        assets: result.assets || [],
                        topRisks: result.topRisks || [],
                        trends: result.trends || {}
                    });
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const { summary, assets, topRisks, trends } = data;

    // Calculate risk level
    const riskLevel = useMemo(() => {
        const score = summary.overallRiskScore || 0;
        if (score >= 80) return 'healthy';
        if (score >= 60) return 'caution';
        if (score >= 40) return 'atRisk';
        return 'critical';
    }, [summary.overallRiskScore]);

    // Generate recommended actions from top risks
    const recommendedActions = useMemo(() => {
        return topRisks.slice(0, 5).map(risk => ({
            title: risk.title || risk.name || 'Unknown Finding',
            asset: risk.assetValue || risk.asset || 'Unknown Asset',
            severity: risk.severity || risk.priority,
            onClick: () => navigate(`/assets/${risk.assetId}#vulnerabilities-${risk.severity}`)
        }));
    }, [topRisks, navigate]);

    // Generate ticker alerts from new high risks
    const tickerAlerts = useMemo(() => {
        const newRisks = trends.newHighRisks || [];
        if (newRisks.length === 0 && topRisks.length > 0) {
            // Fall back to top risks if no new ones
            return topRisks.slice(0, 5).map(r => ({
                title: r.title || r.name,
                asset: r.assetValue || r.asset,
                severity: r.severity || r.priority
            }));
        }
        return newRisks.map(r => ({
            title: r.title || r.name,
            asset: r.assetValue || r.asset,
            severity: r.severity
        }));
    }, [trends.newHighRisks, topRisks]);

    // Trend data for mini chart
    const trendData = useMemo(() => {
        return (trends.riskScoreHistory || []).slice(-7).map((point, i) => ({
            name: `D${i + 1}`,
            score: point.score || point.riskScore || 0
        }));
    }, [trends.riskScoreHistory]);

    if (loading) {
        return (
            <div className="mc-page">
                <header className="mc-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                    <h1>Mission Control</h1>
                </header>
                <SkeletonDashboard />
            </div>
        );
    }

    if (error) {
        return (
            <div className="mc-page">
                <header className="mc-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                    <h1>Mission Control</h1>
                </header>
                <div className="mc-error">
                    <span className="error-icon">⚠️</span>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="mc-page">
            {/* Header */}
            <header className="mc-header">
                <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                <h1>
                    <span className="header-icon">📡</span>
                    Mission Control
                </h1>
                <div className="header-meta">
                    <span className="last-update">
                        Last scan: {formatRelativeTime(summary.lastScanAt)}
                    </span>
                </div>
            </header>

            {/* Alert Ticker */}
            <AlertTicker alerts={tickerAlerts} />

            {/* Main Grid */}
            <div className="mc-main-grid">
                {/* Left Column - Metrics */}
                <div className="mc-left-column">
                    <MetricCard
                        icon="🔴"
                        value={summary.totalVulnerabilities?.critical || 0}
                        label="Critical"
                        color="var(--severity-critical)"
                        pulse={summary.totalVulnerabilities?.critical > 0}
                        onClick={() => navigate('/inventory?severity=critical')}
                    />
                    <MetricCard
                        icon="🟠"
                        value={summary.totalVulnerabilities?.high || 0}
                        label="High"
                        color="var(--severity-high)"
                        onClick={() => navigate('/inventory?severity=high')}
                    />
                    <MetricCard
                        icon="🟡"
                        value={summary.totalVulnerabilities?.medium || 0}
                        label="Medium"
                        color="var(--severity-medium)"
                        onClick={() => navigate('/inventory?severity=medium')}
                    />
                </div>

                {/* Center - Main Gauge */}
                <div className="mc-center">
                    <RiskGauge
                        score={summary.overallRiskScore || 0}
                        riskLevel={riskLevel}
                    />

                    {/* Trend mini-chart */}
                    {trendData.length > 0 && (
                        <div className="mc-trend-mini">
                            <div className="trend-label">7-Day Trend</div>
                            <ResponsiveContainer width="100%" height={60}>
                                <AreaChart data={trendData}>
                                    <defs>
                                        <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={themeColors.risk[riskLevel] || themeColors.accent.primary} stopOpacity={0.3} />
                                            <stop offset="100%" stopColor={themeColors.risk[riskLevel] || themeColors.accent.primary} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <Area
                                        type="monotone"
                                        dataKey="score"
                                        stroke={themeColors.risk[riskLevel] || themeColors.accent.primary}
                                        fill="url(#trendGradient)"
                                        strokeWidth={2}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                {/* Right Column - Assets & Actions */}
                <div className="mc-right-column">
                    <MetricCard
                        icon="🌐"
                        value={summary.totalAssets || 0}
                        label="Assets"
                        color="var(--neon-blue)"
                        onClick={() => navigate('/assets')}
                    />
                    <MetricCard
                        icon="⚠️"
                        value={`${summary.riskDistribution?.critical || 0}/${summary.totalAssets || 0}`}
                        label="At Risk"
                        color="var(--severity-high)"
                        onClick={() => navigate('/command-center')}
                    />
                    <MetricCard
                        icon="🛡️"
                        value={summary.riskDistribution?.healthy || 0}
                        label="Healthy"
                        color="var(--severity-low)"
                        onClick={() => navigate('/assets')}
                    />
                </div>
            </div>

            {/* Bottom Section - Two Columns */}
            <div className="mc-bottom-grid">
                {/* Recommended Actions */}
                <div className="mc-panel mc-actions-panel">
                    <div className="panel-header">
                        <h2>
                            <span className="panel-icon">🎯</span>
                            Top Defense Priorities
                        </h2>
                    </div>
                    <div className="panel-content">
                        {recommendedActions.length > 0 ? (
                            recommendedActions.map((action, i) => (
                                <ActionItem key={i} action={action} index={i} />
                            ))
                        ) : (
                            <div className="empty-state">
                                <span className="empty-icon">✓</span>
                                <p>No critical actions required</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Asset Profiles */}
                <div className="mc-panel mc-assets-panel">
                    <div className="panel-header">
                        <h2>
                            <span className="panel-icon">📁</span>
                            Asset Profiles
                        </h2>
                        <button
                            className="panel-action"
                            onClick={() => navigate('/assets')}
                        >
                            View All →
                        </button>
                    </div>
                    <div className="panel-content">
                        <div className="asset-list-header">
                            <span className="col-status"></span>
                            <span className="col-name">Asset</span>
                            <span className="col-score">Score</span>
                            <span className="col-vulns">Findings</span>
                            <span className="col-time">Last Scan</span>
                            <span className="col-actions">Actions</span>
                        </div>
                        <div className="asset-list-body">
                            {assets.slice(0, 6).map(asset => (
                                <AssetRow
                                    key={asset.assetId}
                                    asset={asset}
                                    onClick={() => navigate(`/assets/${asset.assetId}`)}
                                />
                            ))}
                            {assets.length === 0 && (
                                <div className="empty-state">
                                    <span className="empty-icon">📦</span>
                                    <p>No assets monitored</p>
                                    <button onClick={() => navigate('/assets')}>
                                        Add Your First Asset
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DashboardMissionControl;
